import { computed, ref } from "vue";
import { defineStore } from "pinia";
import {
  ONBOARDING_STEPS,
  completeOnboardingStep,
  dismissOnboardingChecklist,
  getOnboardingChecklist,
} from "@/services/onboarding-checklist.service";
import { analytics } from "@/services/analytics.service";

export { ONBOARDING_STEPS };

export const useOnboardingChecklistStore = defineStore(
  "onboardingChecklist",
  () => {
    const steps = ref([]);
    const completedCount = ref(0);
    const totalCount = ref(0);
    const allComplete = ref(false);
    const dismissed = ref(false);
    const loaded = ref(false);
    const loading = ref(false);

    /**
     * Steps already reported to the server in this page session.
     *
     * The hooks that call markStep (search, opening a summary) fire on views
     * that may never have loaded the checklist, so we cannot always tell from
     * `steps` whether a step is already done. Tracking what we have sent keeps
     * a user who searches twenty times to a single request. The endpoint is
     * idempotent, so the worst case is one redundant POST per session.
     */
    const reportedSteps = ref(new Set());

    /**
     * True once a server response has actually reported the card as dismissed,
     * as opposed to the optimistic flag we set on click. A failed request must
     * never undo a dismissal the server already confirmed.
     */
    const dismissConfirmed = ref(false);

    /**
     * Steps ticked locally, held separately from `steps` so the record survives
     * whatever order things happen in. An upload can finish before the first
     * checklist read returns, and then there is no `steps` array to write into;
     * keeping the set apart means the tick is simply re-applied on every load.
     */
    const locallyCompleted = ref(new Set());

    /**
     * Responses can arrive in any order, and not just because of the network:
     * the server can commit two writes in the opposite order to the one the
     * client issued them, so no client-side sequence number can decide which
     * snapshot is "newer".
     *
     * Both facts this store holds are monotonic, which makes ordering moot:
     * a step never un-completes, and dismissal is one-way because no endpoint
     * un-dismisses. Merging every response into the union of what we know
     * therefore converges on the same state no matter what order things land
     * in. That matters because `reportedSteps` would otherwise refuse to
     * re-report a step that a stale response had quietly unticked.
     */
    const applyChecklist = (data) => {
      if (!data) return;

      const incoming = data.steps ?? [];
      const merged = incoming.map((incomingStep) => {
        const known = steps.value.find((s) => s.step === incomingStep.step);
        if (known?.completed) return known;
        if (locallyCompleted.value.has(incomingStep.step)) {
          return { ...incomingStep, completed: true };
        }
        return incomingStep;
      });

      steps.value = merged;
      completedCount.value = merged.filter((s) => s.completed).length;
      totalCount.value = data.totalCount ?? merged.length;
      allComplete.value = merged.length > 0 && merged.every((s) => s.completed);
      if (data.dismissed) dismissConfirmed.value = true;
      dismissed.value = Boolean(data.dismissed) || dismissed.value;
      loaded.value = true;
    };

    const isStepComplete = (step) =>
      steps.value.some((s) => s.step === step && s.completed);

    /**
     * Tick a step locally without telling the server.
     *
     * For the derived steps there is nothing to tell it — the server works them
     * out from AudioFile / ChatHistory — but those rows are not queryable the
     * instant the client finishes an upload (ConversationsList waits 2s before
     * refreshing for exactly this reason). Reading the checklist in that window
     * returns a stale `false`, so the box the user just earned stays empty.
     *
     * Ticking locally keeps the UI honest about what the user actually did. It
     * is safe because the merge is monotonic: a lagging `false` from the server
     * cannot undo it, and the next read confirms it for good.
     */
    const markStepLocally = (step) => {
      // Recorded first and unconditionally: if the checklist has not loaded yet
      // there is nothing to update, and this is what carries the tick over to
      // whenever it does.
      locallyCompleted.value.add(step);
      if (isStepComplete(step)) return;

      steps.value = steps.value.map((s) =>
        s.step === step ? { ...s, completed: true } : s,
      );
      completedCount.value = steps.value.filter((s) => s.completed).length;
      allComplete.value =
        steps.value.length > 0 && steps.value.every((s) => s.completed);
    };

    /** Load progress from the server. Safe to call repeatedly. */
    const fetchChecklist = async () => {
      if (loading.value) return;
      loading.value = true;
      try {
        const { data, error } = await getOnboardingChecklist();
        if (error) {
          console.error("[OnboardingChecklist] Failed to load:", error);
          return;
        }
        applyChecklist(data);
      } finally {
        loading.value = false;
      }
    };

    /**
     * Report a step as complete. No-ops when we already know it is done or
     * have already reported it, so callers can fire this freely from event
     * handlers without guarding.
     */
    const markStep = async (step) => {
      if (reportedSteps.value.has(step)) return;
      if (loaded.value && isStepComplete(step)) return;

      reportedSteps.value.add(step);

      const { data, error } = await completeOnboardingStep(step);
      if (error) {
        // Allow a later attempt to retry rather than silently losing the step.
        reportedSteps.value.delete(step);
        console.error(`[OnboardingChecklist] Failed to mark "${step}":`, error);
        return;
      }

      // Strictly true only. The field is optional, so treating "not false" as a
      // first completion would count every malformed or older response and
      // inflate the activation numbers this event exists to measure.
      if (data?.success === true && data?.firstCompletion === true) {
        analytics.onboardingStepCompleted({ step });
      }
      applyChecklist(data?.checklist);
    };

    /** Hide the card for good. */
    const dismiss = async () => {
      // Already dismissed, or a dismissal is in flight — the optimistic flag is
      // set synchronously below, so this also serialises concurrent clicks.
      // Without it, one request failing could roll back another that succeeded.
      if (dismissed.value) return;

      // Optimistic: the card should disappear immediately on click.
      dismissed.value = true;
      analytics.onboardingChecklistDismissed({
        completed_count: completedCount.value,
        total_count: totalCount.value,
      });

      const { data, error } = await dismissOnboardingChecklist();
      if (error) {
        // Restore only if no response has confirmed the dismissal meanwhile.
        if (!dismissConfirmed.value) dismissed.value = false;
        console.error("[OnboardingChecklist] Failed to dismiss:", error);
        return;
      }
      applyChecklist(data?.checklist);
    };

    /** True when the card should render in its full, expanded form. */
    const showFullChecklist = computed(
      () => loaded.value && !dismissed.value && !allComplete.value,
    );

    return {
      steps,
      completedCount,
      totalCount,
      allComplete,
      dismissed,
      loaded,
      loading,
      reportedSteps,
      dismissConfirmed,
      showFullChecklist,
      fetchChecklist,
      markStep,
      dismiss,
      isStepComplete,
      markStepLocally,
      locallyCompleted,
    };
  },
);
